import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SortHeader, { sortRows } from "@/components/SortHeader";
import { SignIn, ArrowClockwise, CheckCircle, XCircle, MagnifyingGlass, DeviceMobile, DeviceTablet, Desktop } from "@phosphor-icons/react";
import { dmYhm } from "@/lib/dateFmt";

const formatWhen = (iso) => dmYhm(iso) || "—";

// v1.28.46 — Small icon-and-label cell for the Device column.
function DeviceCell({ device, browser }) {
    if (!device && !browser) return <span className="text-slate-400">—</span>;
    const Icon = device === "Mobile" ? DeviceMobile
              : device === "Tablet" ? DeviceTablet
              : Desktop;
    const label = device === "Mobile" ? "Mobile"
               : device === "Tablet" ? "Tablet"
               : "Desktop";
    return (
        <span className="inline-flex items-center gap-1.5">
            <Icon size={13} weight="regular" className="text-slate-500" />
            <span className="font-medium text-slate-700">{label}</span>
            {browser && <span className="text-slate-500">— {browser}</span>}
        </span>
    );
}

export default function LoginHistory() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState({ by: "timestamp", dir: "desc" });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/login-log");
            setRows(Array.isArray(data) ? data : []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        const list = !t ? rows : rows.filter((r) =>
            (r.email || "").toLowerCase().includes(t)
            || (r.ip || "").includes(t)
            // v1.25.75 — search also matches party name / user name
            || (r.party_name || "").toLowerCase().includes(t)
            || (r.user_name || "").toLowerCase().includes(t)
            || (r.role || "").toLowerCase().includes(t)
            // v1.28.46 — search also matches device + browser
            || (r.device || "").toLowerCase().includes(t)
            || (r.browser || "").toLowerCase().includes(t)
        );
        return sortRows(list, sort);
    }, [rows, q, sort]);

    const successCount = rows.filter((r) => r.success).length;
    const failedCount = rows.length - successCount;

    return (
        <div data-testid="login-history-page" className="space-y-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl font-bold text-slate-900">Login History</h1>
                    <p className="mt-1 text-sm text-slate-500">Last 500 login attempts — successful and failed.</p>
                </div>
                <div className="flex gap-2">
                    <div className="relative">
                        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            data-testid="login-history-search"
                            placeholder="Search email, phone, party name or IP…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="w-64 pl-9"
                        />
                    </div>
                    <Button variant="outline" size="sm" onClick={load}>
                        <ArrowClockwise size={14} className="mr-1.5" /> Refresh
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Total attempts</div>
                    <div className="mt-1 font-display text-2xl font-bold text-slate-900">{rows.length}</div>
                </div>
                <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-4 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wider text-emerald-700">Successful</div>
                    <div className="mt-1 font-display text-2xl font-bold text-emerald-800">{successCount}</div>
                </div>
                <div className="rounded-lg border border-rose-100 bg-rose-50/40 p-4 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wider text-rose-700">Failed</div>
                    <div className="mt-1 font-display text-2xl font-bold text-rose-800">{failedCount}</div>
                </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="px-5 py-3 text-left"><SortHeader sort={sort} onSort={setSort} by="timestamp" label="Time" /></th>
                                <th className="px-5 py-3 text-left"><SortHeader sort={sort} onSort={setSort} by="email" label="Email / Phone" /></th>
                                {/* v1.25.75 — Party / User column so admins see 'Prima Traders'
                                    next to a phone-number login at a glance instead of guessing. */}
                                <th className="px-5 py-3 text-left"><SortHeader sort={sort} onSort={setSort} by="party_name" label="Party / User" /></th>
                                <th className="px-5 py-3 text-left"><SortHeader sort={sort} onSort={setSort} by="success" label="Result" /></th>
                                <th className="px-5 py-3 text-left"><SortHeader sort={sort} onSort={setSort} by="ip" label="IP" /></th>
                                {/* v1.28.46 — Device + browser column */}
                                <th className="px-5 py-3 text-left"><SortHeader sort={sort} onSort={setSort} by="device" label="Device" /></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && rows.length === 0 && (
                                <tr><td colSpan={6} className="px-5 py-14 text-center text-slate-400">Loading…</td></tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={6} className="px-5 py-14 text-center text-slate-400">
                                    <SignIn size={28} className="mx-auto mb-2" />
                                    No login records match.
                                </td></tr>
                            )}
                            {filtered.map((r) => (
                                <tr key={r.id} data-testid={`login-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="px-5 py-3 text-slate-600">{formatWhen(r.timestamp)}</td>
                                    <td className="px-5 py-3 font-medium text-slate-900">{r.email || "—"}</td>
                                    <td className="px-5 py-3" data-testid={`login-party-${r.id}`}>
                                        {r.party_name ? (
                                            <div className="flex flex-col leading-tight">
                                                <span className="font-medium text-slate-900">{r.party_name}</span>
                                                <span className="text-[10px] uppercase tracking-wider text-emerald-700">Party</span>
                                            </div>
                                        ) : r.role === "admin" ? (
                                            <div className="flex flex-col leading-tight">
                                                <span className="font-medium text-slate-900">{r.user_name || "—"}</span>
                                                <span className="text-[10px] uppercase tracking-wider text-indigo-700">Admin</span>
                                            </div>
                                        ) : r.user_name ? (
                                            <span className="text-slate-700">{r.user_name}</span>
                                        ) : (
                                            <span className="text-xs italic text-slate-400">Unknown user</span>
                                        )}
                                    </td>
                                    <td className="px-5 py-3">
                                        {r.success ? (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                                                <CheckCircle size={11} weight="fill" /> Success
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                                                <XCircle size={11} weight="fill" /> Failed
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 font-mono text-xs text-slate-500">{r.ip || "—"}</td>
                                    <td className="px-5 py-3 text-xs text-slate-700" data-testid={`login-device-${r.id}`}>
                                        <DeviceCell device={r.device} browser={r.browser} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
